import { useCallback, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Layout from './layout/Layout';
import { COMMON_PATH, ELECTRICITY_PATH, GAS_PATH } from './utils';
import { NoAuth, RequireAuth } from './auth';
import { useAuth } from './hooks';
import {
    // gas pages
    TheEur,
    TheChf,
    TheProfile,
    TheClosingPrice,
    TtfProfile,
    TtfClosingPrice,
    PegChf,
    PegProfile,
    PegClosingPrice,
    TtfEur,
    TtfChf,
    PegEur,
    Marketplace,
    Intraday,
    Watchlist,
    Overview,
    TradingAdvisor,
    // Market,
    Chat,
    PriceProfiling,
    Settings,
    Notification,

    // energy portfolio
    Dashboard,
    Hedges,
    AutoReporting,
    AutoReportingResult,
    Search,
    Forward,
    Hedge,
    HedgeTransfer,
    Options,
    Counterparty,
    Portfolio,
    Brokers,
    Exchanges,
    // Endpoints,

    // electricity pages
    Eur,
    Chf,

    // common pages
    Home,
    Calendar,
    Orders,
    Disclaimer,
    Help,
    NewsFeed,
    LandingPage,
    Profile,
    ChangePassword,
    UpdatePassword,
    HedgingMargin
} from './pages';
import axios from 'axios';
import {
    apiGetChf,
    apiGetTheEur,
    apiGetMarket,
    apiGetNews,
    apiGetPriceprofile,
    apiGetProfilingRates,
    apiGetProfile,
    apiGetAllUsers,
    apiGetSetPriceProfile,
    apiGetTtfEur,
    apiGetTtfChf,
    apiGetMarketplace,
    apiGetBiogas,
    apiGetIntraday,
    apiGetPegnordEur,
    apiGetPegnordChf,
    apiGetOrder,
    apiGetWatchlist,
    apiGetAccount,
    apiGetEndpoint,
    apiGetLimitOrder,
    apiGetTrailingStop,
    apiGetMinMaxRaw,
    apiGetCheEur,
    apiGetCheChf,
    apiGetAnnouncement,
    apiGetMinMaxDisplay,
    apiGetTrailingStopForDisplay,
    apiGetTtfMinMaxDisplay,
    apiGetTtfTrailingStopForDisplay,
    apiGetPegnordMinMaxDisplay,
    apiGetPegnordTrailingStopForDisplay,
    apiGetMinMaxOrders,
    apiGetMonthHistoricalPrices,
    apiGetQuarterHistoricalPrices,
    apiGetSeasonHistoricalPrices,
    apiGetDisableTrading,
    apiGetHedgeMargin,
    apiGetAnalysisSignal,
    // energy portfolio
    // apiGetClients,
    apiGetPortfolio,
    apiGetExchanges,
    apiGetBroker,
    apiGetCounterParty,
    // apiGetForecasts,
    apiGetPendingHedges
} from '@store';
import { useDispatch } from 'react-redux';
import { SinglePriceProfile, UserManagement } from './pages/gas';
import { apiGetOrdersInfo } from './store/feature/order/orderSlice';
import { MainChatContent } from './pages/common/chat/[id]/MainChatContent';
import { LoginTemp } from './pages/common/chat/temp-login/Login';
import ChatAuthProvider from './pages/common/chat/ChatAuthProvider';
import {
    invalidateChatGroup,
    invalidateChatMessages,
    invalidateUnreadMessageCount,
    useDeliveredChatMessageMutation,
    useGetChatProfileQuery
} from './store/feature/chat/chatApi';
import notificationAudio from '@assets/notification_audio.mp3';
import LayoutWithoutSidebar from './layout/LayoutWithoutSidebar';
import ChatMessageContent from './pages/common/chat/[id]/ChatMessageContent';
import GroupChatMessageContent from './pages/common/chat/[id]/GroupChatMessageContent';
import { ContractDetails } from './components';
import The from './pages/gas/the/The';
import TTF from './pages/gas/ttf/TTF';
import PEG from './pages/gas/pegnord/Pegnord';
import useWS from './utils/socket';
import { setupMessageListener } from './utils/socket';
import Demand from './pages/gas/tradingAdvisor/Demand/Demand';
import TheDAPrice from './pages/gas/the/daPrice/DAPrice';
import TtfDAPrice from './pages/gas/ttf/daPrice/DAPrice';
import PegDAPrice from './pages/gas/pegnord/daPrice/DAPrice';
import TheForwardCurve from './pages/gas/the/forwardCurve/ForwardCurve';
import TtfForwardCurve from './pages/gas/ttf/forwardCurve/ForwardCurve';
import PegForwardCurve from './pages/gas/pegnord/forwardCurve/ForwardCurve';
import EexTab from './pages/gas/eex/EexTab';

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const { auth, setAuth } = useAuth();
    const dispatch = useDispatch();
    const { data: chatUser } = useGetChatProfileQuery();
    const [deliveredMessage] = useDeliveredChatMessageMutation();

    const implementInterceptors = useCallback(() => {
        // Add a request interceptor
        axios.interceptors.request.use(
            function (config) {
                // Do something before request is sent
                return config;
            },
            function (error) {
                // Do something with request error
                return Promise.reject(error);
            }
        );

        // Add a response interceptor
        axios.interceptors.response.use(
            function (response) {
                // Any status code that lie within the range of 2xx cause this function to trigger
                // Do something with response data
                return response;
            },
            function (error) {
                // Any status codes that falls outside the range of 2xx cause this function to trigger
                // Do something with response error

                if (error.response.status === 401) {
                    setAuth({ session: false });
                    localStorage.removeItem('loginState');
                }
                return Promise.reject(error);
            }
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        implementInterceptors();
    }, [implementInterceptors]);

    useEffect(() => {
        if (auth?.session) {
            dispatch(apiGetProfile());
            dispatch(apiGetAllUsers());
            dispatch(apiGetTheEur());
            dispatch(apiGetChf());
            dispatch(apiGetSetPriceProfile());
            dispatch(apiGetNews());
            dispatch(apiGetPriceprofile());
            dispatch(apiGetMarket());
            dispatch(apiGetTtfEur());
            dispatch(apiGetTtfChf());
            dispatch(apiGetMarketplace());
            dispatch(apiGetBiogas());
            dispatch(apiGetIntraday());
            dispatch(apiGetPegnordEur());
            dispatch(apiGetPegnordChf());
            dispatch(apiGetOrder());
            dispatch(apiGetOrdersInfo());
            dispatch(apiGetWatchlist());
            dispatch(apiGetAccount());
            dispatch(apiGetEndpoint());
            dispatch(apiGetLimitOrder());
            dispatch(apiGetTrailingStop());
            dispatch(apiGetMinMaxRaw());
            dispatch(apiGetCheEur());
            dispatch(apiGetCheChf());
            dispatch(apiGetAnnouncement());
            dispatch(apiGetMinMaxDisplay({ product: 'THE', currency: 'EUR' }));
            dispatch(
                apiGetTrailingStopForDisplay({
                    product: 'THE',
                    currency: 'EUR'
                })
            );
            dispatch(
                apiGetTtfMinMaxDisplay({ product: 'TTF', currency: 'EUR' })
            );
            dispatch(
                apiGetTtfTrailingStopForDisplay({
                    product: 'TTF',
                    currency: 'EUR'
                })
            );
            dispatch(
                apiGetPegnordMinMaxDisplay({ product: 'PEG', currency: 'EUR' })
            );
            dispatch(
                apiGetPegnordTrailingStopForDisplay({
                    product: 'PEG',
                    currency: 'EUR'
                })
            );
            dispatch(apiGetMinMaxOrders());
            dispatch(apiGetMonthHistoricalPrices());
            dispatch(apiGetQuarterHistoricalPrices());
            dispatch(apiGetSeasonHistoricalPrices());
            dispatch(apiGetProfilingRates());
            dispatch(apiGetDisableTrading());
            dispatch(apiGetHedgeMargin());
            // NOTE: apiGetAnalysisSignal requires sym & product parameters and is called in contract details components
            // dispatch(apiGetAnalysisSignal());
            // --- ENERGY PORTFOLIO ---
            // dispatch(apiGetClients());
            dispatch(apiGetPortfolio());
            dispatch(apiGetExchanges());
            dispatch(apiGetBroker());
            dispatch(apiGetCounterParty());
            // dispatch(apiGetForecasts());
            dispatch(apiGetPendingHedges());
        }
    }, [auth, dispatch]);

    // call apiGetTheEur in every 20 seconds
    const interval = setInterval(() => {
        if (auth?.session) {
            // gas
            dispatch(apiGetTheEur());
            dispatch(apiGetChf());
            dispatch(apiGetTtfEur());
            dispatch(apiGetTtfChf());
            dispatch(apiGetPegnordEur());
            dispatch(apiGetPegnordChf());
            dispatch(apiGetMarket());
            dispatch(apiGetMarketplace());
            dispatch(apiGetBiogas());
            dispatch(apiGetIntraday());
            dispatch(apiGetWatchlist());
            // notifications
            dispatch(apiGetLimitOrder());
            dispatch(apiGetTrailingStop());
            dispatch(apiGetMinMaxRaw());
            dispatch(apiGetMinMaxDisplay({ product: 'THE', currency: 'EUR' }));
            dispatch(apiGetMinMaxDisplay({ product: 'THE', currency: 'CHF' }));
            dispatch(apiGetTrailingStopForDisplay({ product: 'THE', currency: 'EUR' }));
            dispatch(apiGetTrailingStopForDisplay({ product: 'THE', currency: 'CHF' }));
            dispatch(apiGetTtfMinMaxDisplay({ product: 'TTF', currency: 'EUR' }));
            dispatch(apiGetTtfMinMaxDisplay({ product: 'TTF', currency: 'CHF' }));
            dispatch(apiGetTtfTrailingStopForDisplay({ product: 'TTF', currency: 'EUR' }));
            dispatch(apiGetTtfTrailingStopForDisplay({ product: 'TTF', currency: 'CHF' }));
            dispatch(apiGetPegnordMinMaxDisplay({ product: 'PEG', currency: 'EUR' }));
            dispatch(apiGetPegnordMinMaxDisplay({ product: 'PEG', currency: 'CHF' }));
            dispatch(apiGetPegnordTrailingStopForDisplay({ product: 'PEG', currency: 'EUR' }));
            dispatch(apiGetPegnordTrailingStopForDisplay({ product: 'PEG', currency: 'CHF' }));
            // electricity
            dispatch(apiGetCheEur());
            dispatch(apiGetCheChf());
        }
    }, 20000);

    useEffect(() => {
        return () => clearInterval(interval);
    }, [interval]);

    useEffect(() => {
        // Available routes for non-authenticated users
        const noAuthOnlyRoutes = ['/login', '/update_password'];
        if (!noAuthOnlyRoutes.includes(location.pathname)) {
            if (!auth?.session) {
                navigate('/login');
                localStorage.setItem('loginState', false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth]);

    const { webSocket } = useWS(
        window.location.hostname === 'localhost'
            ? 'ws://localhost:8080/api/ws'
            : `wss://${window.location.hostname}/api/ws`
    );

    const audioRef = useRef(null);

    const playNewMessageSound = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0; // Reset audio to start
            audioRef.current.play().catch((error) => {
                console.error('Failed to play audio:', error);
            });
        } else {
            console.error(
                'Audio element is not initialized. Ensure the user interacts with the document first.'
            );
        }
    };

    const receiveMessage = useCallback(
        async (data) => {
            console.log(
                '[WebSocket] Received:',
                data,
                'Current user:',
                chatUser?.uuid
            );

            if (
                data?.type === 'user_online_status' ||
                data?.type === 'delete_message' ||
                data?.type === 'read_message' ||
                data?.type === 'delivered_message'
            ) {
                invalidateChatMessages(dispatch);
                invalidateUnreadMessageCount(dispatch);
                return;
            }

            if (data?.type === 'group_message') {
                invalidateChatGroup(dispatch);
                invalidateUnreadMessageCount(dispatch);
                return;
            }

            if (data?.type === 'message') {
                // Set document title as New Message for 2 seconds
                document.title = 'New Message';
                setTimeout(() => {
                    document.title = 'PriceBlick';
                }, 2000);

                if (data?.muted !== true) {
                    playNewMessageSound();
                }

                // FIX: Invalidate ALL chat-related tags to ensure sidebar updates
                invalidateChatMessages(dispatch); // Invalidates ['Chat'] - updates sidebar user list and chat messages
                invalidateUnreadMessageCount(dispatch); // Invalidates ['Unread-Message-Count'] - updates unread counts

                if (
                    data?.data?.from !== chatUser?.uuid &&
                    data?.data?.to === chatUser?.uuid
                ) {
                    try {
                        deliveredMessage(data?.data?.uuid);
                    } catch (error) {
                        console.log(error, 'Error in receiveMessage');
                    }
                }
            }
        },
        [chatUser, dispatch, deliveredMessage]
    );

    useEffect(() => {
        console.log('Setting up message listener with websocket:', webSocket);
        setupMessageListener(webSocket, receiveMessage);
    }, [receiveMessage, webSocket]);

    return (
        <>
            <audio ref={audioRef} src={notificationAudio} />

            <Routes>
                <Route element={<NoAuth />}>
                    <Route
                        path={COMMON_PATH.LANDINGPAGE}
                        element={<LandingPage />}
                    />
                    <Route
                        path={COMMON_PATH.UPDATE_PASSWORD}
                        element={<UpdatePassword />}
                    />
                </Route>
                <Route element={<RequireAuth />}>
                    {/* with sidebar layouts */}
                    <Route element={<LayoutWithoutSidebar />}>
                        <Route path={COMMON_PATH.HOME} element={<Home />} />
                        <Route
                            path={COMMON_PATH.PROFILE}
                            element={<Profile />}
                        />
                        <Route
                            path={COMMON_PATH.SETTINGS}
                            element={<Settings />}
                        />
                        <Route
                            path={`${COMMON_PATH.USER_MANAGEMENT}/:account_uuid`}
                            element={<UserManagement />}
                        />
                        <Route
                            path={COMMON_PATH.CHAT_LOGIN}
                            element={<LoginTemp />}
                        />
                        <Route path={COMMON_PATH.CHAT} element={<Chat />} />
                        <Route
                            path={COMMON_PATH.CHANGE_PASSWORD}
                            element={<ChangePassword />}
                        />
                        <Route
                            path={COMMON_PATH.CHAT_LOGIN}
                            element={<LoginTemp />}
                        />
                        <Route
                            path={COMMON_PATH.HEDGING_MARGIN}
                            element={<HedgingMargin />}
                        />
                        <Route element={<ChatAuthProvider />}>
                            <Route path={COMMON_PATH.CHAT} element={<Chat />}>
                                <Route
                                    path={`${COMMON_PATH.CHAT}/:id`}
                                    element={<MainChatContent />}
                                />
                            </Route>
                        </Route>
                    </Route>
                    {/* with sidebar layouts */}
                    <Route element={<Layout />}>
                        {/* gas path */}
                        <Route path={GAS_PATH.EEX} element={<EexTab />} />
                        <Route element={<The />}>
                            <Route
                                path={GAS_PATH.THE_EUR}
                                element={<TheEur />}
                            />
                            <Route
                                path={GAS_PATH.THE_CHF}
                                element={<TheChf />}
                            />
                            <Route
                                path={GAS_PATH.THE_PROFILE}
                                element={<TheProfile />}
                            />
                            <Route
                                path={GAS_PATH.THE_CLOSING_PRICE}
                                element={<TheClosingPrice />}
                            />
                            <Route
                                path={GAS_PATH.THE_DA_PRICE}
                                element={<TheDAPrice />}
                            />
                            <Route
                                path={GAS_PATH.THE_FORWARD_CURVE}
                                element={<TheForwardCurve />}
                            />
                            <Route
                                path={GAS_PATH.THE_CONTRACT_DETAILS}
                                element={<ContractDetails />}
                            />
                        </Route>
                        <Route element={<TTF />}>
                            <Route
                                path={GAS_PATH.TTF_EUR}
                                element={<TtfEur />}
                            />
                            <Route
                                path={GAS_PATH.TTF_CHF}
                                element={<TtfChf />}
                            />
                            <Route
                                path={GAS_PATH.TTF_PROFILE}
                                element={<TtfProfile />}
                            />
                            <Route
                                path={GAS_PATH.TTF_CLOSING_PRICE}
                                element={<TtfClosingPrice />}
                            />
                            <Route
                                path={GAS_PATH.TTF_DA_PRICE}
                                element={<TtfDAPrice />}
                            />
                            <Route
                                path={GAS_PATH.TTF_FORWARD_CURVE}
                                element={<TtfForwardCurve />}
                            />
                            <Route
                                path={GAS_PATH.TTF_CONTRACT_DETAILS}
                                element={<ContractDetails />}
                            />
                        </Route>
                        <Route element={<PEG />}>
                            <Route
                                path={GAS_PATH.PEG_EUR}
                                element={<PegEur />}
                            />
                            <Route
                                path={GAS_PATH.PEG_CHF}
                                element={<PegChf />}
                            />
                            <Route
                                path={GAS_PATH.PEG_PROFILE}
                                element={<PegProfile />}
                            />
                            <Route
                                path={GAS_PATH.PEG_CLOSING_PRICE}
                                element={<PegClosingPrice />}
                            />
                            <Route
                                path={GAS_PATH.PEG_DA_PRICE}
                                element={<PegDAPrice />}
                            />
                            <Route
                                path={GAS_PATH.PEG_FORWARD_CURVE}
                                element={<PegForwardCurve />}
                            />
                            <Route
                                path={GAS_PATH.PEG_CONTRACT_DETAILS}
                                element={<ContractDetails />}
                            />
                        </Route>
                        <Route
                            path={GAS_PATH.TRADING_ADVISOR}
                            element={<Demand />}
                        />
                        <Route
                            path={`${GAS_PATH.TRADING_ADVISOR}/:uuid`}
                            element={<TradingAdvisor />}
                        />{' '}
                        <Route
                            path={GAS_PATH.MARKET_PLACE}
                            element={<Marketplace />}
                        />
                        <Route
                            path={GAS_PATH.INTRADAY}
                            element={<Intraday />}
                        />
                        <Route
                            path={GAS_PATH.CALENDAR}
                            element={<Calendar />}
                        />
                        <Route path={GAS_PATH.ORDERS} element={<Orders />} />
                        <Route
                            path={GAS_PATH.WATCHLIST}
                            element={<Watchlist />}
                        />
                        <Route
                            path={GAS_PATH.OVERVIEW}
                            element={<Overview />}
                        />
                        <Route
                            path={GAS_PATH.NEWS_FEED}
                            element={<NewsFeed />}
                        />
                        <Route
                            path={GAS_PATH.HEDGING_MARGIN}
                            element={<HedgingMargin />}
                        />
                        <Route
                            path={GAS_PATH.CHAT_LOGIN}
                            element={<LoginTemp />}
                        />
                        <Route element={<ChatAuthProvider />}>
                            <Route path={GAS_PATH.CHAT} element={<Chat />}>
                                {/* <Route
                                    path={`${GAS_PATH.CHAT}/:id`}
                                    element={<MainChatContent />}
                                /> */}
                                <Route
                                    path={`${GAS_PATH.CHAT}/:id`}
                                    element={<ChatMessageContent />}
                                />
                                <Route
                                    path={`${GAS_PATH.CHAT}/group/:id`}
                                    element={<GroupChatMessageContent />}
                                />
                            </Route>
                        </Route>
                        <Route
                            path={GAS_PATH.PRICE_PROFILING}
                            element={<PriceProfiling />}
                        />
                        <Route
                            path={`${GAS_PATH.SINGLE_PRICE_PROFILE}/:uuid`}
                            element={<SinglePriceProfile />}
                        />
                        <Route path={GAS_PATH.HELP} element={<Help />} />
                        <Route
                            path={GAS_PATH.SETTINGS}
                            element={<Settings />}
                        />
                        <Route
                            path={`${GAS_PATH.USER_MANAGEMENT}/:account_uuid`}
                            element={<UserManagement />}
                        />
                        <Route
                            path={GAS_PATH.NOTIFICATIONS}
                            element={<Notification />}
                        />
                        <Route path={GAS_PATH.PROFILE} element={<Profile />} />
                        <Route
                            path={GAS_PATH.CHANGE_PASSWORD}
                            element={<ChangePassword />}
                        />
                        {/* energy portfolio */}
                        <Route
                            path={GAS_PATH.DASHBOARD}
                            element={<Dashboard />}
                        />
                        <Route path={GAS_PATH.HEDGES} element={<Hedges />} />
                        <Route
                            path={GAS_PATH.AUTO_REPORTING}
                            element={<AutoReporting />}
                        />
                        <Route
                            path={`${GAS_PATH.AUTO_REPORTING_RESULT}/:uuid`}
                            element={<AutoReportingResult />}
                        />
                        <Route path={GAS_PATH.SEARCH} element={<Search />} />
                        <Route path={GAS_PATH.FORWARD} element={<Forward />} />
                        <Route path={GAS_PATH.HEDGE} element={<Hedge />} />
                        <Route
                            path={GAS_PATH.HEDGE_TRANSFER}
                            element={<HedgeTransfer />}
                        />
                        {/* <Route path={GAS_PATH.OPTIONS} element={<Options />} /> */}
                        <Route
                            path={GAS_PATH.COUNTERPARTY}
                            element={<Counterparty />}
                        />
                        <Route
                            path={GAS_PATH.PORTFOLIO}
                            element={<Portfolio />}
                        />
                        <Route path={GAS_PATH.BROKERS} element={<Brokers />} />
                        <Route
                            path={GAS_PATH.EXCHANGES}
                            element={<Exchanges />}
                        />
                        {/* <Route path={GAS_PATH.ENDPOINTS} element={<Endpoints />} /> */}
                        <Route
                            path={GAS_PATH.SETTINGS}
                            element={<Settings />}
                        />
                        {/* electricity path */}
                        <Route
                            path={ELECTRICITY_PATH.NEWS_FEED}
                            element={<NewsFeed />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.CALENDAR}
                            element={<Calendar />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.CHE_EUR}
                            element={<Eur />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.CH_CHF}
                            element={<Chf />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.ORDERS}
                            element={<Orders />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.DISCLAIMER}
                            element={<Disclaimer />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.HELP}
                            element={<Help />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.PROFILE}
                            element={<Profile />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.CHANGE_PASSWORD}
                            element={<ChangePassword />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.SETTINGS}
                            element={<Settings />}
                        />
                        <Route
                            path={`${ELECTRICITY_PATH.USER_MANAGEMENT}/:account_uuid`}
                            element={<UserManagement />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.HEDGING_MARGIN}
                            element={<HedgingMargin />}
                        />
                        <Route
                            path={ELECTRICITY_PATH.CHAT_LOGIN}
                            element={<LoginTemp />}
                        />
                        <Route element={<ChatAuthProvider />}>
                            <Route
                                path={ELECTRICITY_PATH.CHAT}
                                element={<Chat />}
                            >
                                {/* <Route
                                    path={`${ELECTRICITY_PATH.CHAT}/:id`}
                                    element={<MainChatContent />}
                                /> */}
                                <Route
                                    path={`${ELECTRICITY_PATH.CHAT}/:id`}
                                    element={<ChatMessageContent />}
                                />
                                <Route
                                    path={`${ELECTRICITY_PATH.CHAT}/group/:id`}
                                    element={<GroupChatMessageContent />}
                                />
                            </Route>
                        </Route>
                        {/* common path */}
                    </Route>
                </Route>
            </Routes>
        </>
    );
}

export default App;
